import * as d3 from 'd3';
import { LayoutResult, LayoutNode, LayoutEdge, StyleOptions, LayoutType, TreeNode, TreeEditAction } from './types';
import { pruneNode, pruneNodes, extractSubtree, rerootAt, ladderize, toNewick } from './newick-parser';
import type { TipColorMap, MetadataTable } from './metadata';
import { recolorForVisibleTips } from './metadata';

function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/** Format leaf names: replace underscores with spaces (Newick convention) */
function formatLeafName(name: string): string {
  return name.replace(/_/g, ' ');
}

function escapeForTooltip(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export interface RendererOptions {
  container: HTMLElement;
  layout: LayoutResult;
  style: StyleOptions;
  layoutType: LayoutType;
  onTreeEdit?: (newRoot: TreeNode, action: TreeEditAction) => void;
  onBulkDelete?: (newRoot: TreeNode) => void;
  root?: TreeNode;
  boxSelectMode?: boolean;
  /** Optional tip color map from CSV metadata */
  tipColorMap?: TipColorMap | null;
  /** Full metadata table for tooltips */
  metadataTable?: MetadataTable | null;
  /** Which column in the metadata table holds tip IDs */
  metadataIdColumn?: string;
  /** Whether dark mode is active */
  darkMode?: boolean;
  /** Previous layout to animate from (flip/ladderize transitions) */
  prevLayout?: LayoutResult;
}

/** Default branch/label color; swapped for dark mode when user hasn't customized. */
const DEFAULT_TREE_COLOR = '#1b1b1b';
const DEFAULT_TREE_COLOR_DARK = '#e0e0e0';
function themedColor(userValue: string, darkMode: boolean): string {
  if (darkMode && userValue === DEFAULT_TREE_COLOR) return DEFAULT_TREE_COLOR_DARK;
  return userValue;
}

export class TreeRenderer {
  private svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private g!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private zoom!: d3.ZoomBehavior<SVGSVGElement, unknown>;
  private container: HTMLElement;
  private style: StyleOptions;
  private layoutType: LayoutType;
  private currentLayout: LayoutResult | null = null;
  private onTreeEdit?: (newRoot: TreeNode, action: TreeEditAction) => void;
  private root?: TreeNode;
  private contextMenu: HTMLElement | null = null;
  private tipColorMap?: TipColorMap | null;
  private metadataTable?: MetadataTable | null;
  private metadataIdColumn?: string;
  private tooltip: HTMLElement | null = null;
  private darkMode: boolean = false;
  private boxSelectMode: boolean = false;
  private selectedNodes: Set<TreeNode> = new Set();
  private onBulkDelete?: (newRoot: TreeNode) => void;
  private boxSelectStart: { svgX: number; svgY: number } | null = null;
  private boxSelectRectEl: d3.Selection<SVGRectElement, unknown, null, undefined> | null = null;
  private onSvgMouseDownBound = (e: MouseEvent) => this.onSvgBoxSelectMouseDown(e);
  private onWindowMouseMoveBound = (e: MouseEvent) => this.onBoxSelectMouseMove(e);
  private onWindowMouseUpBound = (e: MouseEvent) => this.onBoxSelectMouseUp(e);
  private onKeyDownBound = (e: KeyboardEvent) => this.onBoxSelectKeyDown(e);
  private dismissContextMenuBound = () => this.dismissContextMenu();

  constructor(private options: RendererOptions) {
    this.container = options.container;
    this.style = options.style;
    this.layoutType = options.layoutType;
    this.onTreeEdit = options.onTreeEdit;
    this.root = options.root;
    this.tipColorMap = options.tipColorMap;
    this.metadataTable = options.metadataTable;
    this.metadataIdColumn = options.metadataIdColumn;
    this.darkMode = !!options.darkMode;
    this.onBulkDelete = options.onBulkDelete;
    this.boxSelectMode = !!options.boxSelectMode;
    this.init();
    this.render(options.layout, options.prevLayout);
  }

  private init(): void {
    d3.select(this.container).selectAll('*').remove();

    this.svg = d3.select(this.container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('class', 'tree-svg');

    this.g = this.svg.append('g').attr('class', 'tree-group');

    this.zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 20])
      .on('zoom', (event) => {
        this.g.attr('transform', event.transform);
      });

    // Dynamic zoom filter: block drag-to-pan in lasso mode, always allow wheel
    this.zoom.filter((event: Event) => {
      if (this.boxSelectMode) return event.type === 'wheel';
      const me = event as MouseEvent;
      return !me.ctrlKey && !me.button;
    });

    this.svg.call(this.zoom);

    // Lasso selection rect (drawn in SVG viewport space, above the tree group)
    this.boxSelectRectEl = this.svg.append('rect')
      .attr('class', 'box-select-rect')
      .attr('fill', 'rgba(0,94,162,0.07)')
      .attr('stroke', '#005ea2')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5,3')
      .attr('pointer-events', 'none')
      .attr('display', 'none');

    // Lasso drag listeners (capture phase fires before d3-zoom handlers)
    this.svg.node()!.addEventListener('mousedown', this.onSvgMouseDownBound, true);
    window.addEventListener('mousemove', this.onWindowMouseMoveBound);
    window.addEventListener('mouseup', this.onWindowMouseUpBound);
    document.addEventListener('keydown', this.onKeyDownBound);

    // Right-click on SVG background shows bulk-delete menu when nodes are selected
    this.svg.on('contextmenu.boxselect', (event: MouseEvent) => {
      if (!this.boxSelectMode || this.selectedNodes.size === 0) return;
      event.preventDefault();
      this.showBulkContextMenu(event.clientX, event.clientY);
    });

    if (this.boxSelectMode) {
      this.svg.style('cursor', 'crosshair');
    }

    // Add zoom controls overlay
    this.addZoomControls();
    this.addSearchBox();
  }

  private addZoomControls(): void {
    const controls = document.createElement('div');
    controls.className = 'zoom-controls';

    const btnFit = document.createElement('button');
    btnFit.className = 'zoom-btn';
    btnFit.title = 'Fit to view';
    btnFit.textContent = 'Fit';
    btnFit.addEventListener('click', () => {
      if (this.currentLayout) this.fitToView(this.currentLayout);
    });

    const btnIn = document.createElement('button');
    btnIn.className = 'zoom-btn';
    btnIn.title = 'Zoom in';
    btnIn.textContent = '+';
    btnIn.addEventListener('click', () => {
      this.svg.transition().duration(200).call(this.zoom.scaleBy, 1.4);
    });

    const btnOut = document.createElement('button');
    btnOut.className = 'zoom-btn';
    btnOut.title = 'Zoom out';
    btnOut.textContent = '\u2212'; // minus sign
    btnOut.addEventListener('click', () => {
      this.svg.transition().duration(200).call(this.zoom.scaleBy, 1 / 1.4);
    });

    controls.append(btnIn, btnOut, btnFit);
    this.container.appendChild(controls);
  }

  private addSearchBox(): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'tree-search';

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Search taxa...';
    input.className = 'tree-search-input';

    let searchTimer: ReturnType<typeof setTimeout>;
    input.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        const query = input.value.trim().toLowerCase();
        this.highlightSearch(query);
      }, 150);
    });

    wrapper.appendChild(input);
    this.container.appendChild(wrapper);
  }

  private highlightSearch(query: string): void {
    if (!query) {
      // Clear all highlights
      this.g.selectAll('text.leaf-label')
        .attr('font-weight', null)
        .attr('opacity', null);
      this.g.selectAll('rect.search-highlight').remove();
      return;
    }

    this.g.selectAll('rect.search-highlight').remove();

    const highlightFill = this.darkMode ? '#5a4a00' : '#fff3cd';
    this.g.selectAll('text.leaf-label').each(function () {
      const el = d3.select(this);
      const text = (el.text() || '').toLowerCase();
      const matches = text.includes(query);

      el.attr('font-weight', matches ? '700' : null)
        .attr('opacity', matches ? null : '0.3');

      if (matches) {
        const bbox = (this as SVGTextElement).getBBox();
        const parent = (this as SVGTextElement).parentNode;
        if (parent) {
          d3.select(parent as Element).insert('rect', ':first-child')
            .attr('class', 'search-highlight')
            .attr('x', bbox.x - 2)
            .attr('y', bbox.y - 1)
            .attr('width', bbox.width + 4)
            .attr('height', bbox.height + 2)
            .attr('rx', 2)
            .attr('fill', highlightFill)
            .attr('opacity', 0.8);
        }
      }
    });
  }

  render(layout: LayoutResult, prevLayout?: LayoutResult): void {
    this.currentLayout = layout;
    this.g.selectAll('*').remove();

    // Animate node/edge positions when a previous layout is provided (flip/ladderize).
    // Only for rectangular layout — radial angle changes are too complex to interpolate.
    const DURATION = 250;
    const animate = !!prevLayout && this.layoutType === 'rectangular';

    const prevNodePos = new Map<TreeNode, LayoutNode>();
    const prevEdgeData = new Map<TreeNode, LayoutEdge>();
    if (animate) {
      for (const n of prevLayout!.nodes) prevNodePos.set(n.node, n);
      for (const e of prevLayout!.edges) prevEdgeData.set(e.targetNode, e);
    }

    // Starting position for a node: old position if available, else final position
    const sx = (d: LayoutNode) => prevNodePos.get(d.node)?.x ?? d.x;
    const sy = (d: LayoutNode) => prevNodePos.get(d.node)?.y ?? d.y;

    // Draw edges
    const edgeGroup = this.g.append('g').attr('class', 'edges');
    const branchColor = themedColor(this.style.branchColor, this.darkMode);

    const rectPath = (d: LayoutEdge) =>
      d.elbowX !== undefined && d.elbowY !== undefined
        ? `M${d.sourceX},${d.sourceY} V${d.elbowY} H${d.targetX}`
        : `M${d.sourceX},${d.sourceY} L${d.targetX},${d.targetY}`;

    if (this.layoutType === 'rectangular') {
      const edgeSel = edgeGroup.selectAll('path.branch')
        .data(layout.edges)
        .enter()
        .append('path')
        .attr('class', 'branch')
        .attr('d', (d) => {
          if (animate) {
            const old = prevEdgeData.get(d.targetNode);
            if (old) return rectPath(old);
          }
          return rectPath(d);
        })
        .attr('fill', 'none')
        .attr('stroke', branchColor)
        .attr('stroke-width', this.style.branchWidth);

      if (animate) {
        edgeSel.transition().duration(DURATION).attr('d', rectPath);
      }
    } else {
      edgeGroup.selectAll('line.branch')
        .data(layout.edges)
        .enter()
        .append('line')
        .attr('class', 'branch')
        .attr('x1', (d) => d.sourceX)
        .attr('y1', (d) => d.sourceY)
        .attr('x2', (d) => d.targetX)
        .attr('y2', (d) => d.targetY)
        .attr('stroke', branchColor)
        .attr('stroke-width', this.style.branchWidth);
    }

    // Draw nodes
    const nodeGroup = this.g.append('g').attr('class', 'nodes');
    const leafNodes = layout.nodes.filter((n) => n.node.children.length === 0);
    const internalNodes = layout.nodes.filter((n) => n.node.children.length > 0);

    // Recolor for only the visible leaves so filtered trees use the most distinguishable colors
    const activeTcm = this.tipColorMap
      ? recolorForVisibleTips(this.tipColorMap, leafNodes.map((n) => n.node.name))
      : null;

    // Tip color lookup: check both raw name and underscore-to-space variants
    const tcm = activeTcm?.colorByTip;
    const dnm = activeTcm?.displayNameByTip;
    const defaultLabelColor = themedColor(this.style.leafLabelColor, this.darkMode);
    const tipColor = (name: string): string => {
      if (!tcm) return defaultLabelColor;
      return tcm.get(name) ?? tcm.get(name.replace(/_/g, ' ')) ?? tcm.get(name.replace(/ /g, '_')) ?? defaultLabelColor;
    };
    const displayName = (name: string): string => {
      if (!dnm) return formatLeafName(name);
      return dnm.get(name) ?? dnm.get(name.replace(/_/g, ' ')) ?? dnm.get(name.replace(/ /g, '_')) ?? formatLeafName(name);
    };

    if (this.style.showLeafLabels) {
      if (this.layoutType === 'rectangular') {
        const leafLabelSel = nodeGroup.selectAll('text.leaf-label')
          .data(leafNodes)
          .enter()
          .append('text')
          .attr('class', 'leaf-label')
          .attr('x', (d) => sx(d) + 6)
          .attr('y', (d) => sy(d))
          .attr('dy', '0.35em')
          .attr('text-anchor', 'start')
          .attr('font-size', this.style.leafLabelSize + 'px')
          .attr('font-family', this.style.fontFamily)
          .attr('fill', (d) => tipColor(d.node.name))
          .attr('font-style', 'italic')
          .text((d) => displayName(d.node.name));

        if (animate) {
          leafLabelSel.transition().duration(DURATION)
            .attr('x', (d) => d.x + 6)
            .attr('y', (d) => d.y);
        }
      } else {
        const cx = layout.width / 2;
        const cy = layout.height / 2;
        nodeGroup.selectAll('text.leaf-label')
          .data(leafNodes)
          .enter()
          .append('text')
          .attr('class', 'leaf-label')
          .attr('x', (d) => d.x)
          .attr('y', (d) => d.y)
          .attr('dy', '0.35em')
          .attr('text-anchor', (d) => {
            const angle = Math.atan2(d.y - cy, d.x - cx);
            return Math.abs(angle) > Math.PI / 2 ? 'end' : 'start';
          })
          .attr('transform', (d) => {
            const angle = Math.atan2(d.y - cy, d.x - cx);
            let deg = (angle * 180) / Math.PI;
            const flip = Math.abs(deg) > 90;
            if (flip) deg += 180;
            return `rotate(${deg},${d.x},${d.y})`;
          })
          .attr('dx', (d) => {
            const angle = Math.atan2(d.y - cy, d.x - cx);
            return Math.abs(angle) > Math.PI / 2 ? '-6' : '6';
          })
          .attr('font-size', this.style.leafLabelSize + 'px')
          .attr('font-family', this.style.fontFamily)
          .attr('fill', (d) => tipColor(d.node.name))
          .attr('font-style', 'italic')
          .text((d) => displayName(d.node.name));
      }
    }

    // Internal node labels
    if (this.style.showInternalLabels) {
      const intLabelSel = nodeGroup.selectAll('text.internal-label')
        .data(internalNodes.filter((n) => n.node.name))
        .enter()
        .append('text')
        .attr('class', 'internal-label')
        .attr('x', (d) => sx(d))
        .attr('y', (d) => sy(d) - 6)
        .attr('text-anchor', 'middle')
        .attr('font-size', this.style.internalLabelSize + 'px')
        .attr('font-family', this.style.fontFamily)
        .attr('fill', this.darkMode ? '#aaa' : '#666')
        .text((d) => d.node.name);

      if (animate) {
        intLabelSel.transition().duration(DURATION)
          .attr('x', (d) => d.x)
          .attr('y', (d) => d.y - 6);
      }
    }

    // Branch length labels
    if (this.style.showBranchLengths) {
      const branchNodes = layout.nodes.filter(
        (n) => n.node.branchLength !== null && n.parentX !== null
      );
      const blSel = nodeGroup.selectAll('text.branch-length')
        .data(branchNodes)
        .enter()
        .append('text')
        .attr('class', 'branch-length')
        .attr('x', (d) => (d.x + (d.parentX ?? d.x)) / 2)
        .attr('y', (d) => {
          if (this.layoutType === 'rectangular') {
            return sy(d) - 4;
          }
          return ((d.y + (d.parentY ?? d.y)) / 2) - 4;
        })
        .attr('text-anchor', 'middle')
        .attr('font-size', (this.style.internalLabelSize - 1) + 'px')
        .attr('font-family', this.style.fontFamily)
        .attr('fill', this.darkMode ? '#888' : '#999')
        .text((d) => d.node.branchLength?.toFixed(4) ?? '');

      if (animate) {
        blSel.transition().duration(DURATION)
          .attr('y', (d) => d.y - 4);
      }
    }

    // Leaf node dots (colored by metadata if available)
    // Check whether any *visible* tip actually has a color (not just the full map)
    const hasTipColors = tcm && tcm.size > 0 && leafNodes.some(
      (n) => tcm.get(n.node.name) ?? tcm.get(n.node.name.replace(/_/g, ' ')) ?? tcm.get(n.node.name.replace(/ /g, '_'))
    );
    const leafDotSel = nodeGroup.selectAll('circle.leaf-node')
      .data(leafNodes)
      .enter()
      .append('circle')
      .attr('class', 'leaf-node')
      .attr('cx', (d) => sx(d))
      .attr('cy', (d) => sy(d))
      .attr('r', hasTipColors ? 4 : 2)
      .attr('fill', (d) => tipColor(d.node.name));

    if (animate) {
      leafDotSel.transition().duration(DURATION)
        .attr('cx', (d) => d.x)
        .attr('cy', (d) => d.y);
    }

    // Legend (when tip colors are active)
    if (hasTipColors && activeTcm?.legend.length) {
      this.renderLegend(activeTcm);
    }

    // Interactive node hit targets (always at final positions — no animation)
    const allClickableNodes = [...internalNodes, ...leafNodes];
    const editCallback = this.onTreeEdit;
    const root = this.root;
    const self = this;

    if (editCallback && root) {
      nodeGroup.selectAll('circle.node-target')
        .data(allClickableNodes)
        .enter()
        .append('circle')
        .attr('class', 'node-target')
        .attr('cx', (d) => d.x)
        .attr('cy', (d) => d.y)
        .attr('r', isTouchDevice() ? 12 : 6)
        .attr('fill', 'transparent')
        .attr('stroke', 'transparent')
        .attr('stroke-width', 1)
        .attr('cursor', 'pointer')
        .on('mouseenter', function (event, d) {
          const hoverFill = self.darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)';
          const hoverStroke = self.darkMode ? '#888' : '#71767a';
          d3.select(this).attr('fill', hoverFill).attr('stroke', hoverStroke);
          if (d.node.children.length === 0) self.showTooltip(event, d.node);
        })
        .on('mousemove', function (event, d) {
          if (d.node.children.length === 0) self.moveTooltip(event);
        })
        .on('mouseleave', function () {
          d3.select(this).attr('fill', 'transparent').attr('stroke', 'transparent');
          self.hideTooltip();
        })
        .on('click', function (_event, d) {
          if (d.node.children.length > 0) {
            d.node.children.reverse();
            editCallback(root, 'flip');
          }
        })
        .on('contextmenu', function (event, d) {
          event.preventDefault();
          event.stopPropagation();
          if (self.boxSelectMode) {
            if (self.selectedNodes.size > 0) {
              self.showBulkContextMenu(event.clientX, event.clientY);
            }
            return;
          }
          self.showContextMenu(event.clientX, event.clientY, d.node, root);
        })
        .each(function (d) {
          // Long-press for touch devices (500ms)
          let longPressTimer: ReturnType<typeof setTimeout> | null = null;
          const el = this as SVGCircleElement;
          el.addEventListener('touchstart', (e) => {
            longPressTimer = setTimeout(() => {
              longPressTimer = null;
              const touch = e.touches[0];
              if (touch) {
                e.preventDefault();
                self.showContextMenu(touch.clientX, touch.clientY, d.node, root);
              }
            }, 500);
          }, { passive: false });
          el.addEventListener('touchend', () => {
            if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
          });
          el.addEventListener('touchmove', () => {
            if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
          });
        });

      // Dismiss context menu on click anywhere
      document.addEventListener('click', this.dismissContextMenuBound);
    }

    // Remove any stale tooltip from previous render
    if (this.tooltip) { this.tooltip.remove(); this.tooltip = null; }

    // Tooltip on hover for leaf labels and leaf dot circles
    this.attachLeafTooltips(nodeGroup);

    // Scale bar (rectangular layout only, when branch lengths exist)
    if (this.layoutType === 'rectangular') {
      this.renderScaleBar(layout);
    }

    // Figure title — rendered last so getBBox captures all other content
    this.renderFigureTitle();
  }

  private renderFigureTitle(): void {
    const titleText = this.style.figureTitle?.trim() ?? '';
    if (!titleText) return;
    const gNode = this.g.node();
    if (!gNode) return;
    const bbox = gNode.getBBox();
    const fontSize = this.style.figureTitleSize;
    this.g.append('text')
      .attr('class', 'figure-title')
      .attr('x', bbox.x + bbox.width / 2)
      .attr('y', bbox.y - fontSize * 0.5)
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize + 'px')
      .attr('font-family', this.style.fontFamily)
      .attr('font-weight', '700')
      .attr('fill', this.darkMode ? '#e0e0e0' : '#1b1b1b')
      .text(titleText);
  }

  private renderLegend(tcm: TipColorMap): void {
    // tcm is already filtered to visible categories and recolored by the caller
    if (!tcm.legend.length) return;

    const svgNode = this.g.node();
    if (!svgNode) return;
    const bbox = svgNode.getBBox();
    const legendMargin = 30;
    const x0 = bbox.x + bbox.width + legendMargin;
    const fontSize = this.style.legendLabelSize;
    const rowHeight = fontSize + 6;
    const dotR = Math.max(3, Math.round(fontSize * 0.45));
    const titleText = this.style.legendTitle?.trim() ?? '';
    const titleHeight = titleText ? fontSize + 8 : 0;
    const y0 = bbox.y + 10 + titleHeight;

    const legendGroup = this.g.append('g').attr('class', 'legend');

    if (titleText) {
      legendGroup.append('text')
        .attr('x', x0)
        .attr('y', bbox.y + 10 + fontSize)
        .attr('font-size', fontSize + 'px')
        .attr('font-family', this.style.fontFamily)
        .attr('font-weight', '600')
        .attr('fill', this.darkMode ? '#e0e0e0' : '#1b1b1b')
        .text(titleText);
    }

    tcm.legend.forEach((item, i) => {
      const y = y0 + i * rowHeight + rowHeight / 2;
      legendGroup.append('circle')
        .attr('cx', x0)
        .attr('cy', y)
        .attr('r', dotR)
        .attr('fill', item.color);

      legendGroup.append('text')
        .attr('x', x0 + dotR + 8)
        .attr('y', y)
        .attr('dy', '0.35em')
        .attr('font-size', fontSize + 'px')
        .attr('font-family', this.style.fontFamily)
        .attr('fill', this.darkMode ? '#e0e0e0' : '#1b1b1b')
        .text(item.category);
    });

    const contentBBox = legendGroup.node()!.getBBox();
    const pad = 8;
    // Use fill + fill-opacity instead of rgba() for SVG viewer compatibility
    legendGroup.insert('rect', ':first-child')
      .attr('x', contentBBox.x - pad)
      .attr('y', contentBBox.y - pad)
      .attr('width', contentBBox.width + pad * 2)
      .attr('height', contentBBox.height + pad * 2)
      .attr('rx', 4)
      .attr('fill', this.darkMode ? '#1e1e1e' : '#ffffff')
      .attr('fill-opacity', 0.9)
      .attr('stroke', this.darkMode ? '#444' : '#dfe1e2')
      .attr('stroke-width', 1);
  }

  /** Look up metadata row for a tip name, trying underscore/space variants */
  private getMetadataRow(name: string): Record<string, string> | null {
    if (!this.metadataTable) return null;
    const idCol = this.metadataIdColumn ?? this.metadataTable.headers[0];
    for (const row of this.metadataTable.rows) {
      const id = row[idCol];
      if (id === name || id === name.replace(/_/g, ' ') || id === name.replace(/ /g, '_')) {
        return row;
      }
    }
    return null;
  }

  /** Attach hover tooltips to leaf labels */
  private attachLeafTooltips(nodeGroup: d3.Selection<SVGGElement, unknown, null, undefined>): void {
    const self = this;
    // Leaf label text — not covered by node-target circles, so needs its own handlers
    nodeGroup.selectAll<SVGTextElement, LayoutNode>('text.leaf-label')
      .on('mouseenter', function (event: MouseEvent, d: LayoutNode) {
        self.showTooltip(event, d.node);
      })
      .on('mousemove', function (event: MouseEvent) {
        self.moveTooltip(event);
      })
      .on('mouseleave', function () {
        self.hideTooltip();
      });
    // Leaf dot circles are covered by node-target circles which
    // already have tooltip handlers (added in the node-target block)
  }

  private showTooltip(event: MouseEvent, node: TreeNode): void {
    this.hideTooltip();

    const tip = document.createElement('div');
    tip.className = 'tip-tooltip';

    const dnm = this.tipColorMap?.displayNameByTip;
    const name = dnm
      ? (dnm.get(node.name) ?? dnm.get(node.name.replace(/_/g, ' ')) ?? dnm.get(node.name.replace(/ /g, '_')) ?? formatLeafName(node.name))
      : formatLeafName(node.name);
    let html = `<div class="tip-tooltip-name">${escapeForTooltip(name)}</div>`;

    if (node.branchLength !== null) {
      html += `<div class="tip-tooltip-row"><span class="tip-tooltip-key">Branch length</span><span>${node.branchLength}</span></div>`;
    }

    const row = this.getMetadataRow(node.name);
    if (row) {
      for (const header of this.metadataTable!.headers) {
        const val = row[header];
        if (val) {
          html += `<div class="tip-tooltip-row"><span class="tip-tooltip-key">${escapeForTooltip(header)}</span><span>${escapeForTooltip(val)}</span></div>`;
        }
      }
    }

    tip.innerHTML = html;
    document.body.appendChild(tip);
    this.tooltip = tip;
    this.moveTooltip(event);
  }

  private moveTooltip(event: MouseEvent): void {
    if (!this.tooltip) return;
    const pad = 12;
    let x = event.clientX + pad;
    let y = event.clientY + pad;
    // Keep within viewport
    const rect = this.tooltip.getBoundingClientRect();
    if (x + rect.width > window.innerWidth) x = event.clientX - rect.width - pad;
    if (y + rect.height > window.innerHeight) y = event.clientY - rect.height - pad;
    this.tooltip.style.left = x + 'px';
    this.tooltip.style.top = y + 'px';
  }

  private hideTooltip(): void {
    if (this.tooltip) { this.tooltip.remove(); this.tooltip = null; }
  }

  private renderScaleBar(layout: LayoutResult): void {
    // Check if any node has branch lengths
    const hasBranchLengths = layout.nodes.some((n) => n.node.branchLength !== null);
    if (!hasBranchLengths) return;

    // Find the root node to compute scale
    const rootNode = layout.nodes.find((n) => n.parentX === null);
    if (!rootNode) return;

    // Find max branch length sum
    const maxBL = layout.nodes
      .filter((n) => n.node.children.length === 0)
      .reduce((max, n) => Math.max(max, n.x), 0);
    const minX = rootNode.x;
    const plotWidth = maxBL - minX;
    if (plotWidth <= 0) return;

    // Find actual max branch length from tree for scaling
    const tree = rootNode.node;
    const totalBranchLen = this.getMaxBranchLengthFromTree(tree);
    if (totalBranchLen <= 0) return;

    // Choose a nice round scale bar value
    const pixelsPerUnit = plotWidth / totalBranchLen;
    const targetBarPx = 80;
    const targetBarValue = targetBarPx / pixelsPerUnit;
    const scaleBarValue = this.niceRound(targetBarValue);
    const scaleBarPx = scaleBarValue * pixelsPerUnit;

    // Position below the bottommost leaf label
    const leafNodes = layout.nodes.filter((n) => n.node.children.length === 0);
    const maxLeafY = leafNodes.reduce((max, n) => Math.max(max, n.y), 0);
    const labelOffset = this.style.leafLabelSize / 2; // half the text height
    const barY = maxLeafY + labelOffset + 20;
    const barX = minX;

    const scaleGroup = this.g.append('g').attr('class', 'scale-bar');
    const scaleColor = themedColor(this.style.branchColor, this.darkMode);

    scaleGroup.append('line')
      .attr('x1', barX)
      .attr('y1', barY)
      .attr('x2', barX + scaleBarPx)
      .attr('y2', barY)
      .attr('stroke', scaleColor)
      .attr('stroke-width', this.style.branchWidth);

    // Tick marks at ends
    scaleGroup.append('line')
      .attr('x1', barX).attr('y1', barY - 3)
      .attr('x2', barX).attr('y2', barY + 3)
      .attr('stroke', scaleColor)
      .attr('stroke-width', this.style.branchWidth);

    scaleGroup.append('line')
      .attr('x1', barX + scaleBarPx).attr('y1', barY - 3)
      .attr('x2', barX + scaleBarPx).attr('y2', barY + 3)
      .attr('stroke', scaleColor)
      .attr('stroke-width', this.style.branchWidth);

    scaleGroup.append('text')
      .attr('x', barX + scaleBarPx / 2)
      .attr('y', barY - 6)
      .attr('text-anchor', 'middle')
      .attr('font-size', (this.style.internalLabelSize) + 'px')
      .attr('font-family', this.style.fontFamily)
      .attr('fill', scaleColor)
      .text(scaleBarValue < 0.001 ? scaleBarValue.toExponential(0) : String(scaleBarValue));
  }

  private getMaxBranchLengthFromTree(node: any): number {
    if (!node.children || node.children.length === 0) return 0;
    return Math.max(
      ...node.children.map(
        (child: any) => (child.branchLength ?? 1) + this.getMaxBranchLengthFromTree(child)
      )
    );
  }

  private niceRound(value: number): number {
    const exp = Math.floor(Math.log10(value));
    const base = Math.pow(10, exp);
    const normalized = value / base;
    if (normalized < 1.5) return base;
    if (normalized < 3.5) return 2 * base;
    if (normalized < 7.5) return 5 * base;
    return 10 * base;
  }

  getCurrentLayout(): LayoutResult | null {
    return this.currentLayout;
  }

  /** Return the current zoom/pan transform */
  getTransform(): d3.ZoomTransform {
    return d3.zoomTransform(this.svg.node()!);
  }

  /** Restore a previously saved zoom/pan transform (no animation) */
  setTransform(t: d3.ZoomTransform): void {
    this.svg.call(this.zoom.transform, t);
  }

  resetZoom(): void {
    this.svg.transition().duration(300).call(
      this.zoom.transform,
      d3.zoomIdentity
    );
  }

  fitToView(layout: LayoutResult): void {
    const containerRect = this.container.getBoundingClientRect();
    const cw = containerRect.width;
    const ch = containerRect.height;

    // Use the actual rendered bounding box (includes labels + legend)
    const gNode = this.g.node();
    const bbox = gNode ? gNode.getBBox() : null;
    const contentW = bbox ? bbox.width + bbox.x : layout.width;
    const contentH = bbox ? bbox.height + bbox.y : layout.height;

    const scale = Math.min(cw / contentW, ch / contentH) * 0.9;
    const tx = (cw - contentW * scale) / 2;
    const ty = (ch - contentH * scale) / 2;

    this.svg.transition().duration(300).call(
      this.zoom.transform,
      d3.zoomIdentity.translate(tx, ty).scale(scale)
    );
  }

  private showContextMenu(x: number, y: number, node: TreeNode, root: TreeNode): void {
    this.dismissContextMenu();
    const menu = document.createElement('div');
    menu.className = 'tree-context-menu';
    menu.style.left = x + 'px';
    menu.style.top = y + 'px';

    const isInternal = node.children.length > 0;
    const isRoot = node === root;
    const nodeName = node.name
      ? node.name.replace(/_/g, ' ')
      : (isInternal ? 'this clade' : 'this leaf');

    const items: { label: string; action: TreeEditAction; disabled?: boolean }[] = [];

    if (isInternal) {
      items.push({ label: 'Flip children', action: 'flip' });
      items.push({ label: 'Ladderize (large first)', action: 'ladderize-desc' });
      items.push({ label: 'Ladderize (small first)', action: 'ladderize-asc' });
    }
    if (!isRoot) {
      items.push({ label: `Remove ${nodeName}`, action: 'prune' });
    }
    if (isInternal && !isRoot) {
      items.push({ label: `Keep only ${nodeName}`, action: 'keep' });
      items.push({ label: `Reroot here`, action: 'reroot' });
    }

    for (const item of items) {
      const el = document.createElement('div');
      el.className = 'tree-context-menu-item';
      el.textContent = item.label;
      el.addEventListener('click', () => {
        this.dismissContextMenu();
        this.executeAction(item.action, node, root);
      });
      menu.appendChild(el);
    }

    document.body.appendChild(menu);
    this.contextMenu = menu;

    // Keep menu in viewport
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      if (rect.right > window.innerWidth) menu.style.left = (x - rect.width) + 'px';
      if (rect.bottom > window.innerHeight) menu.style.top = (y - rect.height) + 'px';
    });
  }

  private dismissContextMenu(): void {
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
  }

  private executeAction(action: TreeEditAction, node: TreeNode, root: TreeNode): void {
    if (!this.onTreeEdit) return;

    let newRoot = root;
    switch (action) {
      case 'flip':
        node.children.reverse();
        break;
      case 'ladderize-desc':
        ladderize(node, false);
        break;
      case 'ladderize-asc':
        ladderize(node, true);
        break;
      case 'prune': {
        const result = pruneNode(root, node);
        if (!result) return; // Can't prune root
        newRoot = result;
        break;
      }
      case 'keep':
        newRoot = extractSubtree(node);
        newRoot.branchLength = null;
        break;
      case 'reroot':
        newRoot = rerootAt(root, node);
        break;
    }
    this.onTreeEdit(newRoot, action);
  }

  setBoxSelectMode(enabled: boolean): void {
    this.boxSelectMode = enabled;
    if (enabled) {
      this.svg.style('cursor', 'crosshair');
    } else {
      this.svg.node()!.style.removeProperty('cursor');
    }
    if (!enabled) {
      this.selectedNodes.clear();
      this.updateNodeHighlights();
      this.boxSelectStart = null;
      this.boxSelectRectEl?.attr('display', 'none');
      this.dismissContextMenu();
    }
  }

  private getSvgPos(e: MouseEvent): { x: number; y: number } {
    const rect = this.svg.node()!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private onSvgBoxSelectMouseDown(e: MouseEvent): void {
    if (!this.boxSelectMode || e.button !== 0) return;
    e.preventDefault();
    this.dismissContextMenu();
    const pos = this.getSvgPos(e);
    this.boxSelectStart = { svgX: pos.x, svgY: pos.y };
    this.boxSelectRectEl?.attr('display', null)
      .attr('x', pos.x).attr('y', pos.y)
      .attr('width', 0).attr('height', 0);
  }

  private onBoxSelectMouseMove(e: MouseEvent): void {
    if (!this.boxSelectMode || !this.boxSelectStart) return;
    const pos = this.getSvgPos(e);
    const x0 = Math.min(this.boxSelectStart.svgX, pos.x);
    const y0 = Math.min(this.boxSelectStart.svgY, pos.y);
    this.boxSelectRectEl
      ?.attr('x', x0).attr('y', y0)
      .attr('width', Math.abs(pos.x - this.boxSelectStart.svgX))
      .attr('height', Math.abs(pos.y - this.boxSelectStart.svgY));
  }

  private onBoxSelectMouseUp(e: MouseEvent): void {
    if (!this.boxSelectMode || !this.boxSelectStart || !this.currentLayout) return;
    const pos = this.getSvgPos(e);
    const dx = Math.abs(pos.x - this.boxSelectStart.svgX);
    const dy = Math.abs(pos.y - this.boxSelectStart.svgY);
    const start = this.boxSelectStart;
    this.boxSelectStart = null;
    this.boxSelectRectEl?.attr('display', 'none');

    if (dx < 4 && dy < 4) {
      if (!e.shiftKey) {
        this.selectedNodes.clear();
        this.updateNodeHighlights();
      }
      return;
    }

    const t = d3.zoomTransform(this.svg.node()!);
    const [tx0, ty0] = t.invert([Math.min(start.svgX, pos.x), Math.min(start.svgY, pos.y)]);
    const [tx1, ty1] = t.invert([Math.max(start.svgX, pos.x), Math.max(start.svgY, pos.y)]);

    if (!e.shiftKey) this.selectedNodes = new Set();
    for (const ln of this.currentLayout.nodes) {
      if (ln.x >= tx0 && ln.x <= tx1 && ln.y >= ty0 && ln.y <= ty1) {
        this.selectedNodes.add(ln.node);
      }
    }
    this.updateNodeHighlights();
  }

  private onBoxSelectKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.boxSelectMode) {
      this.selectedNodes.clear();
      this.updateNodeHighlights();
      this.boxSelectStart = null;
      this.boxSelectRectEl?.attr('display', 'none');
      this.dismissContextMenu();
    }
  }

  private updateNodeHighlights(): void {
    const selFill = this.darkMode ? 'rgba(100,180,255,0.22)' : 'rgba(0,94,162,0.18)';
    const selStroke = this.darkMode ? '#6ab4ff' : '#005ea2';
    this.g.selectAll<SVGCircleElement, LayoutNode>('circle.node-target')
      .attr('fill', (d) => this.selectedNodes.has(d.node) ? selFill : 'transparent')
      .attr('stroke', (d) => this.selectedNodes.has(d.node) ? selStroke : 'transparent');
  }

  private showBulkContextMenu(x: number, y: number): void {
    this.dismissContextMenu();
    const count = this.selectedNodes.size;
    if (count === 0) return;

    const menu = document.createElement('div');
    menu.className = 'tree-context-menu';
    menu.style.left = x + 'px';
    menu.style.top = y + 'px';

    const item = document.createElement('div');
    item.className = 'tree-context-menu-item tree-context-menu-item--danger';
    item.textContent = `Delete ${count} selected node${count === 1 ? '' : 's'}`;
    item.addEventListener('click', () => {
      this.dismissContextMenu();
      this.executeBulkDelete();
    });
    menu.appendChild(item);

    document.body.appendChild(menu);
    this.contextMenu = menu;

    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      if (rect.right > window.innerWidth) menu.style.left = (x - rect.width) + 'px';
      if (rect.bottom > window.innerHeight) menu.style.top = (y - rect.height) + 'px';
    });
  }

  private executeBulkDelete(): void {
    if (!this.root || !this.onBulkDelete || this.selectedNodes.size === 0) return;
    const targets = [...this.selectedNodes];
    this.selectedNodes.clear();
    const newRoot = pruneNodes(this.root, targets);
    this.onBulkDelete(newRoot);
  }

  destroy(): void {
    this.dismissContextMenu();
    this.hideTooltip();
    document.removeEventListener('click', this.dismissContextMenuBound);
    this.svg.node()?.removeEventListener('mousedown', this.onSvgMouseDownBound, true);
    window.removeEventListener('mousemove', this.onWindowMouseMoveBound);
    window.removeEventListener('mouseup', this.onWindowMouseUpBound);
    document.removeEventListener('keydown', this.onKeyDownBound);
    d3.select(this.container).selectAll('*').remove();
  }
}
